/**
 * INTERFACES PARA SISTEMA DE TEMATIZACIÓN PLANA OPTIMIZADA
 * 
 * Este archivo define la estructura de datos completamente plana para la personalización,
 * basada en el análisis de auditoría que identificó las 18 variables realmente utilizadas
 * en la interfaz de usuario.
 * 
 * PRINCIPIOS DEL DISEÑO:
 * - JSON completamente plano (sin anidación)
 * - Solo variables CSS que realmente se usan en la UI
 * - Estructura simplificada para backend y frontend
 * - Compatibilidad total con el sistema actual
 */

/**
 * Interfaz para el objeto de personalización PLANO que viene del backend.
 * Contiene SOLO las 18 variables CSS que realmente se utilizan en la interfaz.
 */
export interface FlatThemeCustomization {
    // COLORES BÁSICOS (4 variables)
    'theme-primary': string;               // Color primario de la marca - USADO EN 7 COMPONENTES
    'theme-primary-hover': string;         // Color primario en hover - USADO EN 1 COMPONENTE
    'theme-secondary': string;             // Color secundario - USADO EN 8 COMPONENTES
    'theme-text-primary': string;          // Color de texto primario - USADO EN 3 COMPONENTES

    // BORDES Y LAYOUT (1 variable)
    'theme-border': string;                // Color de borde estándar - USADO EN 3 COMPONENTES

    // COMPONENTES DEL TOPBAR (3 variables)
    'theme-topbar-bg': string;             // Fondo de barra superior - USADO EN TOPBAR
    'theme-profile-card-bg': string;       // Fondo de tarjeta de perfil - USADO EN TOPBAR
    'theme-arrow-button-bg': string;       // Fondo de botón flecha - USADO EN TOPBAR (2 veces)
    'theme-arrow-button-hover-bg': string; // Fondo de botón flecha en hover - USADO EN TOPBAR

    // NAVEGACIÓN (1 variable)
    'theme-sidebar-active-bg': string;     // Fondo de elementos activos del sidebar - USADO EN NAVBAR

    // COMPONENTES ESPECÍFICOS DE POINT S (2 variables)
    'theme-pending-orders-bg': string;     // Fondo para KPI de pedidos pendientes - USADO EN KPI-CARD
    'theme-pending-orders-border': string; // Borde para KPI de pedidos pendientes - USADO EN KPI-CARD

    // CALENDARIO (1 variable)
    'theme-today-bg': string;              // Fondo para día actual en calendario - USADO EN CALENDAR-SUMMARY

    // ENLACES Y ACCIONES (1 variable)
    'theme-link-color': string;            // Color para enlaces - USADO EN NEWS-CARD

    // CONTADORES DE CLIENTES (2 variables)
    'theme-client-count-bg': string;       // Fondo para contador de clientes - USADO EN CLIENTS
    'theme-client-count-text': string;     // Texto para contador de clientes - USADO EN CLIENTS

    // ICON DEL TOPBAR (1 variable)
    'theme-arrow-button-icon-color': string; // Color del ícono del botón flecha - USADO EN TOPBAR

    // ASSETS OPCIONALES (URLs de imágenes - solo si se usan realmente)
    userAvatarUrl?: string;                // URL del avatar del usuario - USADO EN TOPBAR
    chevronImageUrl?: string;              // URL del ícono chevron - USADO EN TOPBAR  
    logoUrl?: string;                      // URL del logo - USADO EN TOPBAR
}

/**
 * Ejemplo de JSON PLANO de personalización optimizado para Point S.
 * Contiene SOLO las 18 claves que realmente se utilizan.
 * 
 * COMPARACIÓN:
 * - JSON anterior: ~85 propiedades con anidación compleja
 * - JSON optimizado: 18 propiedades completamente planas
 * - Reducción: ~79% menos datos
 */
export const FLAT_POINT_S_CUSTOMIZATION: FlatThemeCustomization = {
    // COLORES BÁSICOS
    'theme-primary': '#e31e24',               // Rojo característico de Point S
    'theme-primary-hover': '#d11820',         // Rojo más oscuro para hover
    'theme-secondary': '#002f5d',             // Azul corporativo Point S
    'theme-text-primary': '#333333',          // Texto principal

    // BORDES Y LAYOUT
    'theme-border': '#e0e0e0',                // Borde gris claro

    // TOPBAR
    'theme-topbar-bg': '#ffffff',             // Fondo blanco para topbar
    'theme-profile-card-bg': '#f8f9fa',       // Fondo gris muy claro para tarjeta de perfil
    'theme-arrow-button-bg': '#f1f3f4',       // Fondo gris claro para botones
    'theme-arrow-button-hover-bg': '#e8eaed', // Fondo gris más oscuro para hover
    'theme-arrow-button-icon-color': '#333333', // Color del ícono del botón

    // NAVEGACIÓN
    'theme-sidebar-active-bg': '#fee6e7',      // Fondo rojo muy claro para elementos activos

    // POINT S ESPECÍFICOS
    'theme-pending-orders-bg': '#fff3cd',      // Fondo amarillo claro para pedidos pendientes
    'theme-pending-orders-border': '#ffeaa7',  // Borde amarillo para pedidos pendientes

    // CALENDARIO
    'theme-today-bg': '#e3f2fd',               // Fondo azul muy claro para hoy

    // ENLACES
    'theme-link-color': '#1976d2',             // Azul para enlaces

    // CLIENTES
    'theme-client-count-bg': '#e8f5e8',        // Fondo verde claro para contador
    'theme-client-count-text': '#2e7d32',       // Texto verde para contador

    // ASSETS POINT S
    userAvatarUrl: 'assets/pointS/picto-s-circle.svg',
    chevronImageUrl: 'assets/icons/chevron-left-blue.svg',
    logoUrl: 'assets/pointS/logo.webp'
};

/**
 * Ejemplo de JSON PLANO para otra marca (hipotético).
 * Muestra cómo el mismo formato se aplica a diferentes marcas.
 */
export const FLAT_CUSTOM_BRAND_CUSTOMIZATION: FlatThemeCustomization = {
    // COLORES BÁSICOS
    'theme-primary': '#0066cc',
    'theme-primary-hover': '#0056b3',
    'theme-secondary': '#28a745',
    'theme-text-primary': '#212529',

    // BORDES Y LAYOUT
    'theme-border': '#dee2e6',

    // TOPBAR
    'theme-topbar-bg': '#ffffff',
    'theme-profile-card-bg': '#f8f9fa',
    'theme-arrow-button-bg': '#e9ecef',
    'theme-arrow-button-hover-bg': '#dee2e6',
    'theme-arrow-button-icon-color': '#495057',

    // NAVEGACIÓN
    'theme-sidebar-active-bg': '#cce5ff',

    // ESPECÍFICOS DE MARCA
    'theme-pending-orders-bg': '#d4edda',
    'theme-pending-orders-border': '#c3e6cb',

    // CALENDARIO
    'theme-today-bg': '#fff3cd',

    // ENLACES
    'theme-link-color': '#0066cc',

    // CLIENTES
    'theme-client-count-bg': '#e2e6ea',
    'theme-client-count-text': '#495057',

    // ASSETS OPCIONALES
    userAvatarUrl: 'assets/icons/user-circle.svg',
    chevronImageUrl: 'assets/icons/chevron-left.svg',
    logoUrl: 'assets/logo-custom.svg'
};

/**
 * Tipo de utilidad para obtener las claves del tema plano.
 * Útil para validación y mapeo dinámico.
 */
export type FlatThemeKey = keyof FlatThemeCustomization;

/**
 * Array con todas las claves del tema plano (requeridas).
 * Útil para iteración y validación.
 */
export const FLAT_THEME_KEYS: FlatThemeKey[] = [
    'theme-primary',
    'theme-primary-hover',
    'theme-secondary',
    'theme-text-primary',
    'theme-border',
    'theme-topbar-bg',
    'theme-profile-card-bg',
    'theme-arrow-button-bg',
    'theme-arrow-button-hover-bg',
    'theme-arrow-button-icon-color',
    'theme-sidebar-active-bg',
    'theme-pending-orders-bg',
    'theme-pending-orders-border',
    'theme-today-bg',
    'theme-link-color',
    'theme-client-count-bg',
    'theme-client-count-text'
];

/**
 * Array con claves opcionales del tema plano (assets).
 * Estas claves pueden estar presentes o no.
 */
export const FLAT_THEME_OPTIONAL_KEYS: (keyof FlatThemeCustomization)[] = [
    'userAvatarUrl',
    'chevronImageUrl',
    'logoUrl'
];

/**
 * Array con todas las claves válidas (requeridas + opcionales).
 */
export const ALL_FLAT_THEME_KEYS = [...FLAT_THEME_KEYS, ...FLAT_THEME_OPTIONAL_KEYS];

/**
 * Función de validación para verificar que un objeto cumple con la estructura plana.
 */
export function validateFlatTheme(theme: any): theme is FlatThemeCustomization {
    if (!theme || typeof theme !== 'object') {
        return false;
    }

    // Verificar que todas las claves requeridas están presentes
    for (const key of FLAT_THEME_KEYS) {
        if (!(key in theme) || typeof theme[key] !== 'string') {
            return false;
        }
    }

    return true;
}

/**
 * DOCUMENTACIÓN DE MIGRACIÓN:
 * 
 * BACKEND:
 * 1. Cambiar el endpoint de personalización para devolver solo estas 18 claves
 * 2. Eliminar todas las claves anidadas y no utilizadas
 * 3. Asegurar que el JSON sea completamente plano
 * 4. Validar que todos los valores sean strings válidos (colores CSS)
 * 
 * FRONTEND:
 * 1. Usar FlatThemeCustomization en lugar de interfaces anidadas
 * 2. Aplicar las variables CSS directamente desde las claves del objeto
 * 3. Eliminar lógica de mapeo complejo de objetos anidados
 * 4. Usar validateFlatTheme() para validar datos del backend
 * 
 * VENTAJAS:
 * - Reducción del 79% en el tamaño del JSON de personalización
 * - Estructura de datos más simple y mantenible
 * - Menos variables CSS que gestionar
 * - Mayor claridad sobre qué se puede personalizar realmente
 * - Mejor rendimiento al procesar menos datos
 */
